import { DocumentType } from '../enums';

export enum RouteStatus {
  Unknown = 'Unknown',
  ToDo = 'To do',
  Done = 'Done',
  Ready = 'Ready', // TODO : Consider separate SectionStatus Later
  Locked = 'Locked',
  Requested = 'Requested'
}

export const ROUTE_STATUS_TO_INDEX: RouteStatus[] = [
  RouteStatus.Unknown, // 0
  RouteStatus.ToDo, // 1
  RouteStatus.Done, // 2
  RouteStatus.Ready, // 3
  RouteStatus.Locked, // 4
  RouteStatus.Requested // 5
];

export interface RouteConfig {
  sections: RouteSection[];
  defaultTask: DefaultTask;
  givenName?: string;
  email?: string;
  verifiedApplicant?: boolean;
  applicantEmail?: string;
}

export interface RouteSection {
  label: SectionLabel;
  route: string;
  status?: RouteStatus;
  taskGroups: RouteTaskGroup[];
}

export interface RouteTaskGroup {
  label: YourInformationTaskGroupLabel | ApplicationTaskGroupLabel | OfferTaskGroupLabel;
  status?: RouteStatus;
  tasks: RouteTask[];
}

// We can't know what this will be at design time
// because it is sourced from a Front End free text field
// The weird syntax is to placate SonarCube
type CustomRequirementTaskLabel = `${string}`;

export interface RouteTask {
  label: YourInformationTaskLabel | ApplicationTaskLabel | OfferTaskLabel | CustomRequirementTaskLabel;
  route: string;
  status?: RouteStatus;
}
export interface DefaultTask {
  label: SectionLabel.Home | YourInformationTaskLabel | ApplicationTaskLabel | OfferTaskLabel;
  route: string;
}

export enum SectionLabel {
  Home = 'Home',
  YourInformation = 'Your information',
  Application = 'Application',
  Offers = 'Offers'
}

export enum YourInformationTaskGroupLabel {
  AboutYou = 'About you',
  YourStudyHistory = 'Your study history'
}

export enum ApplicationTaskGroupLabel {
  PersonalInformation = 'Personal information',
  QualificationsExperience = 'Qualifications & experience',
  SupportingInformation = 'Supporting information',
  AboutYourApplication = 'About your application',
  Consent = 'Consent'
}

export enum OfferTaskGroupLabel {
  Acceptance = 'Acceptance',
  Conditions = 'Conditions',
  Payment = 'Payment'
}

export enum YourInformationTaskLabel {
  ContactDetails = 'Contact details',
  FeeStatus = 'Fee status',
  StudyPlans = 'Study plans',
  LatestEducation = 'Your latest education',
  LatestQualifications = 'Your latest qualifications'
}

export enum ApplicationTaskLabel {
  Identity = 'Identity',
  NationalityResidence = 'Nationality & residence',
  ContactDetails = 'Contact details',
  EducationHistory = 'Education history',
  EnglishLanguage = 'English language',
  Employment = 'Employment',
  References = 'References',
  PersonalStatement = 'Personal statement',
  Funding = 'Funding',
  ImmigrationHistory = 'Immigration history',
  DiversityInclusion = 'Diversity & inclusion',
  Representation = 'Representation',
  Referral = 'Referral',
  Declarations = 'Declarations',
  InformationProcessing = 'Information processing',
  PrivacyPolicy = 'Privacy policy',
  TermsConditions = 'Terms & conditions',
  AccuracyOfData = 'Accuracy of data'
}

export enum OfferTaskLabel {
  ConditionalOffer = 'Conditional offer',
  UnconditionalOffer = 'Unconditional offer',
  Passport = 'Passport',
  PhotoID = 'Photo ID',
  AcademicResults = 'Academic requirement', //TODO: update this requirementId with sensible name on migration script writing
  AcademicCertificate = 'Academic certificate',
  AcademicTranscript = 'Academic transcript',
  EnglishLanguageResults = 'English language results',
  EnglishLanguageCertificate = 'Proof of English proficiency',
  ProofOfManagerialExperience = 'Proof of 2 years of management experience',
  ProofOfAddress = 'Proof of address',
  ProofOfNameChange = 'Proof of name change',
  NameAffidavit = 'Name Affidavit',
  BirthCert = 'Birth certificate',
  CV = 'CV to support application',
  ProofOfImmigrationStatus = 'Proof of immigration status',
  LetterOfRecommendation = 'Letter of recommendation',
  PersonalStatement = 'Personal statement',
  Portfolio = 'Portfolio',
  Payment = 'Payment',
  ProofOfPayment = 'Proof of payment',
  CustomRequirement = 'Custom requirement'
}

export enum SectionRoute {
  Home = '/',
  YourInformation = '/your-information',
  Application = '/applications/{applicationId}',
  Offers = '/offers/{offerId}'
}

export enum AgentSectionRoute {
  Home = '/application/{uni}/{applicantId}',
  YourInformation = '/application/{uni}/{applicantId}/your-information',
  Application = '/application/{uni}/{applicantId}/applications/{applicationId}',
  Offers = '/application/{uni}/{applicantId}/offers/{offerId}'
}

export enum YourInformationTaskRoute {
  ContactDetails = '/your-information/contact-details',
  FeeStatus = '/your-information/fee-status',
  StudyPlans = '/your-information/study-plans',
  LatestEducation = '/your-information/your-latest-education',
  LatestQualifications = '/your-information/qualifications'
}

export enum ApplicationTaskRoute {
  Identity = '/applications/{applicationId}/identity',
  NationalityResidence = '/applications/{applicationId}/nationality-and-residence',
  ContactDetails = '/applications/{applicationId}/contact-details',
  EducationHistory = '/applications/{applicationId}/education-history',
  EnglishLanguage = '/applications/{applicationId}/english-language',
  Employment = '/applications/{applicationId}/employment',
  References = '/applications/{applicationId}/references',
  PersonalStatement = '/applications/{applicationId}/personal-statement',
  Funding = '/applications/{applicationId}/funding',
  ImmigrationHistory = '/applications/{applicationId}/immigration-history',
  DiversityInclusion = '/applications/{applicationId}/diversity-inclusion',
  Representation = '/applications/{applicationId}/representation',
  Referral = '/applications/{applicationId}/referral',
  Declarations = '/applications/{applicationId}/declarations',
  InformationProcessing = '/applications/{applicationId}/information-processing',
  PrivacyPolicy = '/applications/{applicationId}/privacy-policy',
  TermsConditions = '/applications/{applicationId}/terms-conditions',
  AccuracyOfData = '/applications/{applicationId}/accuracy-of-data'
}

export enum OfferTaskRoute {
  ConditionalOffer = '/offers/{offerId}/offer-acceptance',
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  UnconditionalOffer = '/offers/{offerId}/offer-acceptance',

  Passport = '/offers/{offerId}/passport',
  PhotoID = '/offers/{offerId}/photo-id',
  AcademicResults = '/offers/{offerId}/academic-results',
  AcademicCertificate = '/offers/{offerId}/academic-certificate',
  AcademicTranscript = '/offers/{offerId}/academic-transcript',
  EnglishLanguageResults = '/offers/{offerId}/english-language-results',
  EnglishLanguageCertificate = '/offers/{offerId}/english-language-certificate',
  ProofOfManagerialExperience = '/offers/{offerId}/proof-of-managerial-experience',
  ProofOfAddress = '/offers/{offerId}/proof-of-address',
  ProofOfNameChange = '/offers/{offerId}/proof-of-name-change',
  NameAffidavit = '/offers/{offerId}/name-affidavit',
  BirthCert = '/offers/{offerId}/birth-cert',
  CV = '/offers/{offerId}/cv',
  ProofOfImmigrationStatus = '/offers/{offerId}/proof-of-immigration-status',
  LetterOfRecommendation = '/offers/{offerId}/letter-of-recommendation',
  PersonalStatement = '/offers/{offerId}/personal-statement',
  Payment = '/offers/{offerId}/payment',
  ProofOfPayment = '/offers/{offerId}/proof-of-payment',
  Portfolio = '/offers/{offerId}/portfolio',
  CustomRequirement = `/offers/{offerId}/{requirementId}`
}

export type DocumentsPerRoute = Partial<
  Record<YourInformationTaskRoute | ApplicationTaskRoute | OfferTaskRoute, DocumentType[]>
>;

export const documentsPerRoute: DocumentsPerRoute = {
  [YourInformationTaskRoute.LatestEducation]: [DocumentType.AcademicTranscript],
  [ApplicationTaskRoute.Identity]: [DocumentType.Passport, DocumentType.PhotoId],
  [ApplicationTaskRoute.EducationHistory]: [DocumentType.AcademicTranscript],
  [ApplicationTaskRoute.PersonalStatement]: [DocumentType.PersonalStatement],
  [ApplicationTaskRoute.Employment]: [DocumentType.Cv],
  [ApplicationTaskRoute.References]: [DocumentType.ReferenceLetter],
  [ApplicationTaskRoute.EnglishLanguage]: [DocumentType.EnglishTest],
  [OfferTaskRoute.AcademicTranscript]: [DocumentType.AcademicTranscript],
  [OfferTaskRoute.CV]: [DocumentType.Cv],
  [OfferTaskRoute.EnglishLanguageCertificate]: [DocumentType.EnglishTest],
  [OfferTaskRoute.LetterOfRecommendation]: [DocumentType.ReferenceLetter],
  [OfferTaskRoute.Passport]: [DocumentType.Passport],
  [OfferTaskRoute.PersonalStatement]: [DocumentType.PersonalStatement],
  [OfferTaskRoute.PhotoID]: [DocumentType.PhotoId],
  [OfferTaskRoute.Portfolio]: [DocumentType.Portfolio],
  [OfferTaskRoute.ProofOfAddress]: [DocumentType.ProofOfAddress],
  [OfferTaskRoute.ProofOfNameChange]: [DocumentType.ProofOfNameChange],
  [OfferTaskRoute.NameAffidavit]: [DocumentType.NameAffidavit],
  [OfferTaskRoute.BirthCert]: [DocumentType.BirthCert],
  [OfferTaskRoute.ProofOfImmigrationStatus]: [DocumentType.ProofOfImmigrationStatus],
  [OfferTaskRoute.ProofOfManagerialExperience]: [DocumentType.ProofOfManagerialExperience],
  [OfferTaskRoute.CustomRequirement]: [DocumentType.CustomRequirement]
};
